import { useEffect, useState } from "react";
import { api } from "../api/client";
import { useLabels } from "../state/hooks";

const SWATCHES = [
  "var(--lb-red-2)", "var(--lb-orange-2)", "var(--lb-amber-2)", "var(--lb-green-2)",
  "var(--lb-teal-2)", "var(--lb-blue-2)", "var(--lb-violet-2)", "var(--lb-magenta-2)",
];

export function LabelPicker({
  mailboxId,
  threadId,
  appliedLabelIds,
  onChange,
  onClose,
}: {
  mailboxId: string;
  threadId: string;
  appliedLabelIds: string[];
  onChange: () => void;
  onClose: () => void;
}) {
  const { labels, reload } = useLabels(mailboxId);
  const [name, setName] = useState("");
  const [color, setColor] = useState(SWATCHES[0]!);
  const [error, setError] = useState<string | null>(null);
  // Held locally (not just derived from the appliedLabelIds prop) so a click
  // reflects instantly instead of waiting on the parent's full thread-detail
  // round trip — and reverts itself if the request actually fails.
  const [applied, setApplied] = useState(new Set(appliedLabelIds));

  useEffect(() => {
    setApplied(new Set(appliedLabelIds));
  }, [appliedLabelIds]);

  async function toggle(labelId: string) {
    const wasApplied = applied.has(labelId);
    setError(null);
    setApplied((prev) => {
      const next = new Set(prev);
      if (wasApplied) next.delete(labelId);
      else next.add(labelId);
      return next;
    });
    try {
      await api.post(`/api/mailboxes/${mailboxId}/threads/${threadId}/action`, {
        action: wasApplied ? "label_remove" : "label_add",
        labelId,
      });
      onChange();
    } catch (e) {
      // Revert the optimistic flip — otherwise a failed request leaves the
      // checkbox showing a state the server never actually recorded.
      setApplied((prev) => {
        const next = new Set(prev);
        if (wasApplied) next.add(labelId);
        else next.delete(labelId);
        return next;
      });
      setError(e instanceof Error ? e.message : "Couldn't update that label.");
    }
  }

  async function createAndApply() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setError(null);

    // If this name already exists (very likely right after a Gmail label
    // import), just apply the existing one instead of round-tripping a
    // create that the server would reject as a duplicate.
    const existing = labels.find((l) => l.name.toLowerCase() === trimmed.toLowerCase());
    if (existing) {
      setName("");
      if (!applied.has(existing.id)) await toggle(existing.id);
      return;
    }

    try {
      const res = await api.post<{ id: string }>(`/api/mailboxes/${mailboxId}/labels`, { name: trimmed, color });
      await api.post(`/api/mailboxes/${mailboxId}/threads/${threadId}/action`, { action: "label_add", labelId: res.id });
      setName("");
      setApplied((prev) => new Set(prev).add(res.id));
      reload();
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't create that label.");
    }
  }

  return (
    <div className="gm-modal-scrim" style={{ zIndex: 42, alignItems: "flex-start" }} onClick={onClose}>
      <div
        className="card card-raised gm-in"
        style={{ width: 280, marginTop: "18vh", padding: 11.2, display: "flex", flexDirection: "column", gap: 4 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="section-header" style={{ marginBottom: 4 }}>Labels</div>
        <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
          {labels.map((l) => (
            <label key={l.id} className="gm-nav-row" style={{ cursor: "pointer" }}>
              <input type="checkbox" checked={applied.has(l.id)} onChange={() => toggle(l.id)} style={{ marginRight: 2 }} />
              <span style={{ width: 8, height: 8, borderRadius: 2, background: l.color, flexShrink: 0 }} />
              {l.name}
            </label>
          ))}
          {labels.length === 0 && <div style={{ padding: 8, fontSize: 11.5, color: "var(--gm-text-4)" }}>No labels yet — create one below.</div>}
        </div>
        {error && <div style={{ fontSize: 11.5, color: "var(--lb-red-2)" }}>{error}</div>}
        <div style={{ borderTop: "1px solid var(--gm-line-soft)", marginTop: 6, paddingTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", gap: 4 }}>
            {SWATCHES.map((s) => (
              <button
                key={s}
                onClick={() => setColor(s)}
                style={{ width: 16, height: 16, borderRadius: 4, background: s, border: color === s ? "2px solid var(--gm-text)" : "none", cursor: "pointer" }}
              />
            ))}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              className="input"
              placeholder="New label…"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createAndApply()}
              autoFocus
            />
            <button className="btn btn-primary" onClick={createAndApply}>Add</button>
          </div>
        </div>
      </div>
    </div>
  );
}
